import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useSettings } from '../hooks/useSettings';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageActions } from '../contexts/PageActionsContext';
import { QrCode, Power, RefreshCw, Trash2 } from 'lucide-react';
import { TooltipHelp } from '../components/TooltipHelp';

// Componente para exibir contador do QR Code
function QRCountdown({ expiresAt }: { expiresAt: Date }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const difference = expiry - now;
      return Math.max(0, Math.ceil(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft <= 0) {
    return <span className="text-red-600 text-sm font-normal">QR Code expirado</span>;
  }

  return (
    <span className="text-orange-600 text-sm font-normal">
      Expira em {timeLeft}s
    </span>
  );
}

interface WhatsAppSession {
  name: string; // Nome real usado na API (ex: vendas_c52982e8)
  displayName?: string; // Nome exibido ao usuário (ex: vendas)
  status: 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
  provider: 'WAHA' | 'EVOLUTION';
  qr?: string;
  qrExpiresAt?: Date;
  me?: {
    id: string;
    pushName: string;
  };
  
  // ✨ v1.4.3: Contadores de sincronização (Evolution API)
  contactsCount?: number;
  chatsCount?: number;
  messagesCount?: number;
  countsUpdatedAt?: Date;
  
  // ✨ v1.4.4: Datas de conexão/desconexão
  connectedAt?: Date;
  disconnectedAt?: Date;
}

// Helper function to strip underscore and ID from session name
function getDisplayName(session: WhatsAppSession): string {
  if (session.displayName) {
    return session.displayName;
  }
  // Remove underscore and ID suffix (8 hexadecimal characters)
  // Pattern: name_ID (where ID is 8 hex chars like "43bd86b0")
  const idPattern = /_[a-f0-9]{8}$/i;
  if (idPattern.test(session.name)) {
    return session.name.replace(idPattern, '');
  }
  // Fallback: if no ID pattern found, try removing last underscore and everything after
  const lastUnderscoreIndex = session.name.lastIndexOf('_');
  if (lastUnderscoreIndex > 0) {
    return session.name.substring(0, lastUnderscoreIndex);
  }
  return session.name;
}

export function WhatsAppConnectionsPage() {
  const { settings } = useSettings();
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const { theme } = useTheme();
  const { isUrlLogin } = useAuth();
  const { registerActions, clearActions, registerMetadata } = usePageActions();
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionProvider, setNewSessionProvider] = useState<'WAHA' | 'EVOLUTION'>('EVOLUTION');
  const [isCreating, setIsCreating] = useState(false);
  const [loadingQR, setLoadingQR] = useState<string | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQRSession, setCurrentQRSession] = useState<WhatsAppSession | null>(null);
  const [createSessionModalOpen, setCreateSessionModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // ✨ v1.4.10: Loading para atualização manual de contadores
  
  // Modal de confirmação de exclusão
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  
  // Estado para ordenação
  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'status' | null;
    direction: 'asc' | 'desc';
  }>(() => {
    // Carregar configuração salva do localStorage
    const saved = localStorage.getItem('whatsapp_sort_config');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { column: null, direction: 'asc' };
      }
    }
    return { column: null, direction: 'asc' };
  });

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Salvar configuração de ordenação no localStorage
  useEffect(() => {
    localStorage.setItem('whatsapp_sort_config', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Função para ordenar sessões
  const sortSessions = (sessions: WhatsAppSession[]) => {
    const sorted = [...sessions];
    
    // Se não há ordenação manual, aplicar padrão
    if (!sortConfig.column) {
      return sorted.sort((a, b) => {
        // 1. Conectados primeiro
        const statusOrder: Record<string, number> = { 
          'WORKING': 0, 
          'SCAN_QR_CODE': 1, 
          'STOPPED': 2, 
          'FAILED': 3 
        };
        const statusDiff = (statusOrder[a.status] || 999) - (statusOrder[b.status] || 999);
        if (statusDiff !== 0) return statusDiff;
        
        // 2. Ordem alfabética
        const nameA = getDisplayName(a).toLowerCase();
        const nameB = getDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }
    
    // Ordenação manual por coluna
    return sorted.sort((a, b) => {
      let aValue: any, bValue: any;
      
      if (sortConfig.column === 'name') {
        aValue = getDisplayName(a).toLowerCase();
        bValue = getDisplayName(b).toLowerCase();
        
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      } else if (sortConfig.column === 'status') {
        const statusOrder: Record<string, number> = { 
          'WORKING': 0, 
          'SCAN_QR_CODE': 1, 
          'STOPPED': 2, 
          'FAILED': 3 
        };
        aValue = statusOrder[a.status] || 999;
        bValue = statusOrder[b.status] || 999;
        
        const comparison = aValue - bValue;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }
      
      return 0;
    });
  };

  // Handler para clique no cabeçalho
  const handleSort = (column: 'name' | 'status') => {
    setSortConfig((prev) => {
      // Se é a mesma coluna, alternar direção
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        } else {
          // Terceiro clique: volta para ordenação padrão
          return { column: null, direction: 'asc' };
        }
      }
      
      // Nova coluna: começa com ASC
      return { column, direction: 'asc' };
    });
  };

  useEffect(() => {
    // Não carregar sessões até que o tenant esteja definido
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    // Recarregar sessões quando o tenant mudar
    loadSessions();

    // Iniciar polling a cada 5 segundos
    const interval = setInterval(() => {
      loadSessions(false); // Não mostrar loading durante polling automático
    }, 5000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedTenantId, tenantLoading]); // Recarrega quando o tenant mudar ou terminar de carregar

  // Polling para verificar se a conexão foi estabelecida quando o modal QR está aberto
  useEffect(() => {
    if (!qrModalOpen || !currentQRSession) {
      return;
    }

    const checkConnection = async () => {
      try {
        const response = await authenticatedFetch('/api/waha/sessions');
        if (response.ok) {
          const sessions = await response.json();
          const updatedSession = sessions.find((s: any) => s.name === currentQRSession.name);

          if (updatedSession) {
            // Se a sessão mudou para WORKING (conectada)
            if (updatedSession.status === 'WORKING' && updatedSession.me) {
              // Fechar modal
              setQrModalOpen(false);
              setCurrentQRSession(null);

              // Atualizar lista de sessões
              loadSessions(false);

              // Mostrar notificação de sucesso
              toast.success(`WhatsApp conectado com sucesso! Logado como: ${updatedSession.me.pushName}`);
              
              // ✨ v1.4.5: Aguardar 2s e atualizar novamente para garantir que contadores foram salvos
              setTimeout(() => {
                console.log('🔄 Atualizando sessões para pegar contadores...');
                loadSessions(false);
              }, 2000);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status da conexão:', error);
      }
    };

    // Verificar imediatamente
    checkConnection();

    // Depois verificar a cada 5 segundos
    const interval = setInterval(checkConnection, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [qrModalOpen, currentQRSession]);

  const loadSessions = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await authenticatedFetch('/api/waha/sessions');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Processar dados das sessões incluindo QR code salvo no banco
      const processedSessions = data.map((session: any) => ({
        name: session.name,
        displayName: session.displayName || session.name,
        status: session.status || 'STOPPED',
        provider: session.provider || 'WAHA',
        me: session.me || null,
        qr: session.qr || null,
        qrExpiresAt: session.qrExpiresAt ? new Date(session.qrExpiresAt) : undefined,
        
        // ✨ v1.4.3: Contadores de sincronização
        contactsCount: session.contactsCount,
        chatsCount: session.chatsCount,
        messagesCount: session.messagesCount,
        countsUpdatedAt: session.countsUpdatedAt ? new Date(session.countsUpdatedAt) : undefined,
        
        // ✨ v1.4.4: Datas de conexão/desconexão
        connectedAt: session.connectedAt ? new Date(session.connectedAt) : undefined,
        disconnectedAt: session.disconnectedAt ? new Date(session.disconnectedAt) : undefined
      }));

      setSessions(processedSessions);
      if (showLoading) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      if (showLoading) {
        toast.error('Erro ao carregar sessões WhatsApp');
        setLoading(false);
      }
    }
  };

  // ✨ v1.4.10: Atualizar contadores manualmente (botão Atualizar)
  const handleRefreshCounters = async () => {
    setRefreshing(true);
    
    try {
      // Filtrar apenas sessões conectadas da Evolution API
      const connectedEvolutionSessions = sessions.filter(
        s => s.status === 'WORKING' && s.provider === 'EVOLUTION'
      );
      
      if (connectedEvolutionSessions.length === 0) {
        toast('Nenhuma sessão Evolution API conectada para atualizar', { icon: 'ℹ️' });
        setRefreshing(false);
        return;
      }
      
      console.log(`📊 Atualizando contadores de ${connectedEvolutionSessions.length} sessão(ões)...`);
      
      // Atualizar contadores de todas as sessões conectadas em paralelo
      const promises = connectedEvolutionSessions.map(session =>
        authenticatedFetch(`/api/waha/sessions/${session.name}/refresh-counters`, {
          method: 'POST'
        })
      );
      
      const results = await Promise.allSettled(promises);
      
      // Verificar resultados
      let successCount = 0;
      let errorCount = 0;
      
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.ok) {
          successCount++;
        } else {
          errorCount++;
          if (result.status === 'rejected') {
            console.error('Erro ao atualizar contador:', result.reason);
          }
        }
      }
      
      // Recarregar lista de sessões para pegar valores atualizados
      await loadSessions(false);
      
      // Mostrar feedback
      if (successCount > 0) {
        toast.success(`Contadores atualizados! ${successCount} sessão(ões) atualizadas`);
      }
      
      if (errorCount > 0) {
        toast.error(`Erro ao atualizar ${errorCount} sessão(ões)`);
      }
      
    } catch (error) {
      console.error('Erro ao atualizar contadores:', error);
      toast.error('Erro ao atualizar contadores');
    } finally {
      setRefreshing(false);
    }
  };

  // 🎯 Actions for iframe mode navigation bar
  const whatsappActions = (
    <div className="flex gap-2">
      <button
        onClick={() => setCreateSessionModalOpen(true)}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
      >
        + Nova Sessão
      </button>
      <button
        onClick={handleRefreshCounters}
        disabled={refreshing || sessions.filter(s => s.status === 'WORKING' && s.provider === 'EVOLUTION').length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {refreshing ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Atualizando...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </>
        )}
      </button>
    </div>
  );

  // 🎯 Register actions and metadata for iframe mode
  useEffect(() => {
    if (isUrlLogin) {
      registerActions('/whatsapp', whatsappActions);
      registerMetadata('/whatsapp', {
        title: 'Conexões WhatsApp',
        subtitle: `${sessions.length} ${sessions.length === 1 ? 'sessão ativa' : 'sessões ativas'}`
      });
    }
    return () => clearActions('/whatsapp');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrlLogin, createSessionModalOpen, refreshing, sessions.length]);

  const createSession = async () => {
    if (!newSessionName.trim()) {
      toast.error('Digite um nome para a sessão');
      return;
    }

    // Validar se as configurações do provedor estão disponíveis
    // if (newSessionProvider === 'WAHA' && (!settings?.wahaHost || !settings?.wahaApiKey)) {
    //   toast.error('Configure as credenciais Whats QR Code - API nas configurações do sistema');
    //   return;
    // }

    if (newSessionProvider === 'EVOLUTION' && (!settings?.evolutionHost || !settings?.evolutionApiKey)) {
      toast.error('Configure as credenciais Evolution API nas configurações do sistema');
      return;
    }

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/api/waha/sessions', {
        method: 'POST',
        body: JSON.stringify({
          name: newSessionName.trim(),
          provider: newSessionProvider
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

        // Verificar se é erro de quota
        if (errorData.upgradeRequired || (errorData.message && errorData.message.includes('Limite'))) {
          toast.error(errorData.message || 'Limite de conexões atingido. Faça upgrade do seu plano para continuar.', {
            duration: 6000,
            icon: '⚠️'
          });
          throw new Error(errorData.message);
        }

        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const providerName = newSessionProvider === 'EVOLUTION' ? 'Whats QR Code - Web' : 'Whats QR Code - API';
      toast.success(`Sessão ${providerName} criada com sucesso`);
      setNewSessionName('');
      // ✨ v1.4.8: Manter o provider selecionado ao invés de resetar para WAHA
      // setNewSessionProvider('WAHA'); // REMOVIDO: causava erro ao criar múltiplas instâncias

      // Recarregar imediatamente
      await loadSessions();

      // Aguardar mais um pouco e recarregar novamente para pegar status atualizado
      setTimeout(() => {
        loadSessions(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao criar sessão:', error);
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error instanceof Error && error.message.includes('Limite'))) {
        toast.error(error instanceof Error ? error.message : 'Erro ao criar sessão');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const deleteSession = async (sessionName: string) => {
    setSessionToDelete(sessionName);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionToDelete}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão removida com sucesso');
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
      loadSessions();
    } catch (error) {
      console.error('Erro ao remover sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao remover sessão');
    }
  };

  const cancelDeleteSession = () => {
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  };

  const restartSession = async (sessionName: string) => {
    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/restart`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão reiniciada');

      // Aguardar um pouco antes de recarregar
      setTimeout(() => {
        loadSessions(false);
      }, 1000);
    } catch (error) {
      console.error('Erro ao reiniciar sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao reiniciar sessão');
    }
  };

  const requestQRCode = async (sessionName: string) => {
    setLoadingQR(sessionName);
    try {
      // Primeiro, iniciar a sessão
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${startResponse.status}`);
      }

      toast.success('QR Code solicitado. Aguarde...');

      // Aguardar um pouco e abrir modal para mostrar QR
      setTimeout(async () => {
        await openQRModal(sessionName);
        loadSessions(false);
      }, 2000);

    } catch (error) {
      console.error('Erro ao solicitar QR Code:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao solicitar QR Code');
    } finally {
      setLoadingQR(null);
    }
  };

  const openQRModal = async (sessionName: string) => {
    try {
      // Primeiro, verificar se já temos um QR salvo no banco para esta sessão
      const currentSession = sessions.find(s => s.name === sessionName);

      if (currentSession?.qr && currentSession?.qrExpiresAt && currentSession.qrExpiresAt > new Date()) {
        // Usar QR code já salvo no banco se ainda não expirou
        const sessionWithQR: WhatsAppSession = {
          name: sessionName,
          status: 'SCAN_QR_CODE',
          provider: currentSession.provider,
          qr: currentSession.qr,
          qrExpiresAt: currentSession.qrExpiresAt,
          me: currentSession.me
        };

        setCurrentQRSession(sessionWithQR);
        setQrModalOpen(true);
        return;
      }

      // Primeiro, iniciar a sessão para gerar o QR
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const startError = await startResponse.json();
        toast.error(`Erro ao iniciar sessão: ${startError.error || 'Erro desconhecido'}`);
        return;
      }

      // Aguardar um pouco para a sessão inicializar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Agora buscar QR através do backend (que vai rotear para API correta)
      const qrResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/auth/qr`);

      if (qrResponse.ok) {
        const qrData = await qrResponse.json();

        if (qrData.qr) {
          const sessionWithQR: WhatsAppSession = {
            name: sessionName,
            status: 'SCAN_QR_CODE',
            provider: currentSession?.provider || 'WAHA',
            qr: qrData.qr,
            qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 60000),
            me: undefined
          };

          setCurrentQRSession(sessionWithQR);
          setQrModalOpen(true);

          // Recarregar sessões para obter dados atualizados do banco
          setTimeout(() => {
            loadSessions(false);
          }, 1000);
        } else {
          toast.error('QR Code não disponível');
        }
      } else {
        const errorData = await qrResponse.json();
        toast.error(errorData.error || 'Erro ao buscar QR Code');
      }
    } catch (error) {
      console.error('Erro ao buscar QR Code:', error);
      toast.error('Erro ao buscar QR Code');
    }
  };

  const closeQRModal = () => {
    setQrModalOpen(false);
    setCurrentQRSession(null);
    // Recarregar sessões para verificar se conectou
    loadSessions(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'bg-green-100 text-green-800';
      case 'SCAN_QR_CODE':
        return 'bg-yellow-100 text-yellow-800';
      case 'STOPPED':
        return 'bg-gray-100 text-gray-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'Conectado';
      case 'SCAN_QR_CODE':
        return 'Aguardando QR Code';
      case 'STOPPED':
        return 'Parado';
      case 'FAILED':
        return 'Erro';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Carregando conexões...</span>
      </div>
    );
  }

  return (
    <>
      {/* Header - Only show in normal mode */}
      {!isUrlLogin && (
      <Header
        title="Conexões WhatsApp"
        subtitle={`${sessions.length} ${sessions.length === 1 ? 'sessão ativa' : 'sessões ativas'}`}
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => setCreateSessionModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
            >
              + Nova Sessão
            </button>
            <button
              onClick={handleRefreshCounters}
              disabled={refreshing || sessions.filter(s => s.status === 'WORKING' && s.provider === 'EVOLUTION').length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {refreshing ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Atualizando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Atualizar
                </>
              )}
            </button>
          </div>
        }
      />
      )}

      <div className="p-6 space-y-6">

      {/* Lista de Sessões */}
      <div className="rounded-lg shadow transition-colors" style={{
        backgroundColor: 'var(--bg-card)',
        borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
      }}>
        <div className="px-6 py-4 border-b transition-colors" style={{ borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)' }}>
          <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span>Sessões Ativas</span>
            <TooltipHelp id="whatsapp_sessions_index" />
          </h3>
        </div>

        {sessions.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>Nenhuma sessão WhatsApp encontrada. Crie uma nova sessão para começar.</p>
          </div>
        ) : (
          <>
            {/* Cabeçalhos Clicáveis */}
            <div 
              className="grid grid-cols-12 gap-4 px-6 py-3 border-b text-sm"
              style={{ 
                borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)',
                backgroundColor: theme === 'dark' ? '#1F1F23' : '#F9FAFB',
                color: 'var(--text-secondary)'
              }}
            >
              <button
                onClick={() => handleSort('name')}
                className="col-span-5 flex items-center gap-2 hover:opacity-70 transition-opacity text-left font-semibold"
                style={{ color: sortConfig.column === 'name' ? 'var(--astra-dark-blue)' : 'inherit' }}
              >
                <span>Nome da Sessão</span>
                {sortConfig.column === 'name' && (
                  <span className="text-lg">
                    {sortConfig.direction === 'asc' ? '⬆️' : '⬇️'}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => handleSort('status')}
                className="col-span-3 flex items-center gap-2 hover:opacity-70 transition-opacity text-left font-semibold"
                style={{ color: sortConfig.column === 'status' ? 'var(--astra-dark-blue)' : 'inherit' }}
              >
                <span>Status</span>
                {sortConfig.column === 'status' && (
                  <span className="text-lg">
                    {sortConfig.direction === 'asc' ? '⬆️' : '⬇️'}
                  </span>
                )}
              </button>
              
              <div className="col-span-4 text-right font-semibold">Ações</div>
            </div>

            {/* Lista de Sessões Ordenadas */}
            <div className="divide-y transition-colors" style={{ borderColor: theme === 'dark' ? '#2A2A2A' : 'var(--border-color)' }}>
              {sortSessions(sessions).map((session) => (
              <div key={session.name} className="p-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Nome e Provider */}
                  <div className="col-span-5">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-base font-normal" style={{ color: 'var(--text-primary)' }}>{getDisplayName(session)}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-normal ${session.provider === 'EVOLUTION' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                        {session.provider === 'EVOLUTION' ? '🚀 Web' : '🔗 API'}
                      </span>
                    </div>
                    {session.me && (
                      <>
                        <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                          <span>{session.me.pushName}</span>
                        </p>
                        
                        {/* ✨ v1.4.3: Contadores de sincronização */}
                        {session.provider === 'EVOLUTION' && (session.contactsCount || session.chatsCount || session.messagesCount) ? (
                          <div className="flex gap-4 mt-2 font-normal">
                            <div className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                              <span className="text-xs">📱</span>
                              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{session.contactsCount || 0}</span>
                              <span className="text-xs">Contatos</span>
                            </div>
                            <div className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                              <span className="text-xs">💬</span>
                              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{session.chatsCount || 0}</span>
                              <span className="text-xs">Chats</span>
                            </div>
                            <div className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                              <span className="text-xs">📨</span>
                              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{session.messagesCount || 0}</span>
                              <span className="text-xs">Mensagens</span>
                            </div>
                          </div>
                        ) : null}
                        
                        {/* ✨ v1.4.4: Datas de conexão/desconexão */}
                        {session.provider === 'EVOLUTION' && (
                          <div className="flex gap-4 mt-4 font-normal" style={{ color: 'var(--text-secondary)' }}>
                            {session.connectedAt && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs">🟢</span>
                                <span className="text-xs">Conectado em:</span>
                                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                                  {new Date(session.connectedAt).toLocaleString('pt-BR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </span>
                              </div>
                            )}
                            {session.disconnectedAt && session.status !== 'WORKING' && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs">🔴</span>
                                <span className="text-xs">Desconectado em:</span>
                                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                                  {new Date(session.disconnectedAt).toLocaleString('pt-BR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-span-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-normal ${getStatusColor(session.status)}`}>
                      {getStatusText(session.status)}
                    </span>
                  </div>

                  {/* Ações */}
                  <div className="col-span-4 flex gap-2 justify-end">
                    {session.status === 'STOPPED' && (
                      <button
                        onClick={() => requestQRCode(session.name)}
                        disabled={loadingQR === session.name}
                        className="px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
                        title="Conectar"
                      >
                        <Power size={16} />
                        <span className="sr-only">{loadingQR === session.name ? 'Solicitando...' : 'Conectar'}</span>
                      </button>
                    )}
                    {session.status === 'SCAN_QR_CODE' && (
                      <button
                        onClick={() => openQRModal(session.name)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-1.5"
                        title="Ver QR Code"
                      >
                        <QrCode size={16} />
                        <span className="sr-only">Ver QR Code</span>
                      </button>
                    )}
                    <button
                      onClick={() => restartSession(session.name)}
                      className="px-3 py-1 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 flex items-center gap-1.5"
                      title="Reiniciar"
                    >
                      <RefreshCw size={16} />
                      <span className="sr-only">Reiniciar</span>
                    </button>
                    <button
                      onClick={() => deleteSession(session.name)}
                      className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 flex items-center gap-1.5"
                      title="Remover"
                    >
                      <Trash2 size={16} />
                      <span className="sr-only">Remover</span>
                    </button>
                  </div>
                </div>

              </div>
            ))}
            </div>
          </>
        )}
      </div>

      {/* Modal Criar Sessão */}
      {createSessionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl p-8 w-full max-w-md border transition-colors" style={{
            background: theme === 'dark' ? '#1D1E24' : 'var(--bg-card)',
            borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
          }} role="dialog" aria-labelledby="create-session-title">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 id="create-session-title" className="text-xl font-semibold text-n-slate-12">
                Nova Sessão WhatsApp
              </h2>
              <p className="mt-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                Digite um nome para identificar esta sessão
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="session-provider" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Provedor WhatsApp *
                </label>
                <select
                  id="session-provider"
                  value={newSessionProvider}
                  onChange={(e) => setNewSessionProvider(e.target.value as 'WAHA' | 'EVOLUTION')}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm font-normal transition-colors bg-white text-gray-900"
                  style={{
                    borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
                  }}
                  disabled={isCreating}
                >
                  {/* <option value="WAHA">🔗 Whats QR Code - API</option> */}
                  <option value="EVOLUTION">🚀 Whats QR Code - Web</option>
                </select>
                <p className={`text-xs font-normal mt-2 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                  Escolha o provedor para conectar ao WhatsApp
                </p>
              </div>

              <div>
                <label htmlFor="session-name" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Nome da Sessão *
                </label>
                <input
                  id="session-name"
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder="Ex: vendas, suporte, atendimento"
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm font-normal transition-colors bg-white text-gray-900"
                  style={{
                    borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
                  }}
                  disabled={isCreating}
                  autoFocus
                />
                <p className={`text-xs font-normal mt-2 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                  Use um nome descritivo para facilitar a identificação
                </p>
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setCreateSessionModalOpen(false);
                    setNewSessionName('');
                    setNewSessionProvider('WAHA');
                  }}
                  className="flex-1 py-3 px-6 rounded-xl hover:opacity-80 font-medium transition-all duration-200 border"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
                  }}
                  disabled={isCreating}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await createSession();
                    setCreateSessionModalOpen(false);
                  }}
                  disabled={isCreating || !newSessionName.trim()}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Criando...
                    </>
                  ) : (
                    'Criar Sessão'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do QR Code */}
      {qrModalOpen && currentQRSession && (() => {
        // console.log('🎨 Renderizando modal QR:', {
        //   qrModalOpen,
        //   hasCurrentQRSession: !!currentQRSession,
        //   currentQRSessionName: currentQRSession?.name,
        //   hasQR: !!currentQRSession?.qr,
        //   qrLength: currentQRSession?.qr?.length
        // });
        return (
          <div className="qr-modal-overlay">
            <div className="qr-modal-content bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold text-n-slate-12">
                QR Code - {getDisplayName(currentQRSession)} ({currentQRSession.provider === 'EVOLUTION' ? 'Whats QR Code - Web' : 'Whats QR Code - API'})
              </h3>
              <button
                onClick={closeQRModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {currentQRSession?.qr ? (
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-4 text-n-slate-12">Escaneie o QR Code com seu WhatsApp</h3>
                  <p className="text-sm font-normal text-gray-600 mb-6">
                    Abra o WhatsApp no seu celular, vá em <strong>Configurações → Aparelhos conectados → Conectar aparelho</strong> e escaneie o código abaixo
                  </p>
                  <div className="bg-white p-6 rounded-lg border inline-block shadow-lg">
                    {(() => {
                      // Para Evolution: QR vem em base64 direto
                      if (currentQRSession.provider === 'EVOLUTION' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                          />
                        );
                      }

                      // Para WAHA: QR já vem processado pelo backend em base64
                      if (currentQRSession.provider === 'WAHA' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                            onError={(e) => {
                              console.error('Erro ao carregar QR WAHA base64');
                            }}
                          />
                        );
                      }

                      // Fallback: detectar automaticamente
                      const qrSrc = currentQRSession.qr?.startsWith('data:image')
                        ? currentQRSession.qr
                        : `/api/waha/sessions/${currentQRSession.name}/auth/qr`;

                      return (
                        <img
                          src={qrSrc}
                          alt="QR Code WhatsApp"
                          className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                          onError={(e) => {
                            console.error('Erro ao carregar QR fallback');
                          }}
                        />
                      );
                    })()}
                  </div>
                  {currentQRSession.qrExpiresAt && (
                    <div className="mt-4">
                      <QRCountdown expiresAt={currentQRSession.qrExpiresAt} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <span className="text-sm font-normal text-gray-600">Gerando QR Code...</span>
                  <p className="text-xs font-normal text-gray-500 mt-2">Aguarde um momento</p>
                </div>
              )}

            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => openQRModal(currentQRSession.name)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              >
                Atualizar QR
              </button>
              <button
                onClick={closeQRModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      </div>

      {/* Modal de Confirmação de Exclusão de Sessão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
            background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
          }}>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-4" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                Confirmar Exclusão
              </h3>
              <p className="mb-6" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                Tem certeza que deseja remover a sessão "{sessionToDelete}"?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDeleteSession}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                    color: theme === 'dark' ? 'white' : '#374151'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteSession}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                  style={{
                    backgroundColor: '#dc2626'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                >
                  Remover
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}